import {
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { environment } from '@environments';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { QuillEditorComponent, QuillModules } from 'ngx-quill';
import type Quill from 'quill';
import { Subscription } from 'rxjs';
import { LinkController } from 'src/app/domain/controllers/link.controller';
import { LinkEntity } from 'src/app/domain/entities/link.entitty';
import { RaClickOutsideDirective } from '../../directives/ra-click-outside/ra-click-outside.directive';
import { RaIconComponent } from '../ra-icon/ra-icon.component';
import { DATA_TAG_CONTEXT } from './data-tag-context.token';
import { refreshDataTagValidityInRoot, syncDataTagDisplay } from './data-tag-display.sync';
import { getDataTagHint, getDataTagLabel } from './data-tag-i18n';
import { getLinkCategoryIcon } from './link-category-icon';
import {
    DATA_TAG_BLOT_NAME,
    DATA_TAG_EMPTY_CLASS,
    ensureDataTagMeta,
    getDataTagFromInsert,
    isDataTagInsert,
    registerDataTagBlot,
} from './quill-data-tag';
import { registerSpanStyleFormats } from './quill-span-style';
import {
    DATA_LINK_ATTR,
    DATA_LINK_DESC_ATTR,
    DATA_LINK_EMPTY_CLASS,
    encodeDataLinkFormat,
    registerLinkPlaceholderBlot,
} from './quill-link-placeholder';

type DeltaOp = { insert: string | object; attributes?: Record<string, unknown> };
type DeltaOps = DeltaOp[];
type ColorPickerMode = 'color' | 'background';
type QuillRange = { index: number; length: number };
type ColorPickerPosition = { top: number; left: number; width: number; maxHeight: number };

@Component({
    selector: 'ra-editor',
    templateUrl: 'ra-editor.component.html',
    styleUrl: 'ra-editor.component.scss',
    imports: [
        FormsModule,
        QuillEditorComponent,
        RaIconComponent,
        TranslatePipe,
        RaClickOutsideDirective,
    ],
})
export class RaEditorComponent implements OnInit, OnDestroy {
    private _translate = inject(TranslateService);

    // * [Inputs]
    public value = input.required<string | Record<string, unknown>>();
    public placeholder = input<string>();
    public limitCharacters = input<number>(environment.LIMITES?.MAX_CHARACTERS);
    public showToolbar = input<boolean>(true);
    public disabled = input<boolean>(false);
    public enableLinkAutocomplete = input<boolean>(true);
    public showLimitCharacters = input<boolean>(true);
    public submitOnEnter = input<boolean>(false);
    public compact = input<boolean>(false);
    /**
     * Modo INLINE: o editor é o próprio texto da folha (sem borda, sem padding, cresce com o
     * conteúdo) e quem formata é a barra do topo do editor, por `format()`. Escape tira o
     * foco. Ver docs/PLANO-EDICAO-INLINE.md.
     */
    public inline = input<boolean>(false);

    // * [Outputs]
    public change = output<string>();
    public onFocus = output<void>();
    public onLeave = output<string>();
    public submit = output<string>();
    /**
     * Formatos da seleção atual (negrito, cor, tamanho, fonte...) e se ela cobre algum trecho,
     * a cada mudança de seleção ou de formato.
     */
    public formatChange = output<{ format: Record<string, unknown>; hasSelection: boolean }>();

    public valueEditor = '';
    public modules: QuillModules = { toolbar: false };
    private quill: Quill | null = null;
    private lastValidValue = '';
    private lastEmittedValue = '';
    private lastSelectionRange: QuillRange | null = null;
    public hasFocus = signal(false);
    private windowBlurHandler?: () => void;
    private visibilityChangeHandler?: () => void;

    // * [Color picker]
    public showColorPicker = signal(false);
    public colorPickerMode = signal<ColorPickerMode>('color');
    public colorHue = signal(100);
    public colorSaturation = signal(65);
    public colorBrightness = signal(45);
    public selectedColorHex = signal('#455F27');
    private _paletteDragMoveHandler?: (event: MouseEvent) => void;
    private _paletteDragUpHandler?: () => void;
    public colorPickerPosition = signal<ColorPickerPosition>({
        top: 16,
        left: 16,
        width: 320,
        maxHeight: 520,
    });
    public quickColors = [
        '#FFFFFF',
        '#000000',
        '#E5E7EB',
        '#9CA3AF',
        '#374151',
        '#3381FF',
        '#2A89EF',
        '#15BE78',
        '#FF850A',
        '#E04F4F',
        '#6366F1',
        '#8B5CF6',
        '#EC4899',
        '#F59E0B',
        '#10B981',
        '#3B82F6',
        '#14B8A6',
        '#F97316',
    ];

    // * [Autocomplete]
    public showAutocompleteList = signal(false);
    public autocompleteSearch = signal('');
    /** -1 = nenhum item focado; a 1ª seta pra baixo foca o primeiro */
    public autocompleteIndex = signal(-1);
    public autocompletePosition = signal<{ top: number; left: number } | null>(null);
    private justInsertedLink = false;
    private textChangeHandler?: (delta: unknown, oldDelta: unknown, source: string) => void;
    private selectionChangeHandler?: (range: unknown, oldRange: unknown, source: string) => void;
    private _langVersion = signal(0);
    private _langChangeSub?: Subscription;

    // * [Tooltip nos links]
    private linkTooltipElement: HTMLElement | null = null;
    private linkTooltipShowTimeout: ReturnType<typeof setTimeout> | null = null;
    private linkTooltipHideTimeout: ReturnType<typeof setTimeout> | null = null;
    private linkTooltipMouseEnter?: (e: MouseEvent) => void;
    private linkTooltipMouseLeave?: (e: MouseEvent) => void;

    // * [Injects]
    private _linkController = inject(LinkController);
    private _host = inject(ElementRef);
    private _dataTagContext = inject(DATA_TAG_CONTEXT, { optional: true });

    private _filteredLinks = computed(() => {
        this._langVersion();
        const search = this.autocompleteSearch().toUpperCase().trim();
        const links = this._linkController.links();
        if (!search) return links;
        return links.filter(link => {
            const tag = this._normalizeLinkValue(link.value ?? link.tagName ?? '');
            const val = tag.toUpperCase();
            const tagName = (link.tagName ?? '').toUpperCase();
            const legacyName = (link.name ?? '').toUpperCase();
            const desc = (link.description ?? '').toUpperCase();
            const label = this._getDataTagLabel(tag).toUpperCase();
            const catDesc = (link.categoryDescription ?? '').toUpperCase();
            const searchNoDollar = search.replace(/^\$/, '');
            return (
                val.includes(searchNoDollar) ||
                val.includes(search) ||
                tagName.includes(search) ||
                tagName.includes(searchNoDollar) ||
                legacyName.includes(search) ||
                legacyName.includes(searchNoDollar) ||
                desc.includes(search) ||
                desc.includes(searchNoDollar) ||
                label.includes(search) ||
                label.includes(searchNoDollar) ||
                catDesc.includes(search) ||
                catDesc.includes(searchNoDollar)
            );
        });
    });

    public filteredLinks = this._filteredLinks;

    /**
     * Links agrupados por categoria para exibição. O `flatIndex` é numerado
     * DEPOIS da ordenação dos grupos: a navegação por teclado precisa seguir a
     * ordem da tela, não a ordem em que a API devolveu os links.
     */
    public filteredLinksByCategory = computed(() => {
        const links = this._filteredLinks();
        const groups = new Map<string, LinkEntity[]>();
        for (const link of links) {
            const cat = this._getLinkCategoryLabel(link);
            if (!groups.has(cat)) groups.set(cat, []);
            groups.get(cat)!.push(link);
        }
        let flatIndex = 0;
        return Array.from(groups.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([categoryDescription, links]) => ({
                categoryDescription,
                items: links.map(link => ({ link, flatIndex: flatIndex++ })),
            }));
    });

    /** Lista achatada na ordem exibida — base da navegação por teclado. */
    public displayLinks = computed(() =>
        this.filteredLinksByCategory().flatMap(group => group.items.map(item => item.link))
    );

    public readonly hintText = 'Digite @ para links de dados';
    public colorPickerTitle = computed(() =>
        this.colorPickerMode() === 'background'
            ? 'Selecionar cor de fundo do texto'
            : 'Selecionar cor do texto'
    );
    public colorPaletteBackground = computed(
        () =>
            `linear-gradient(to top, #000000, transparent), linear-gradient(to right, #ffffff, hsl(${this.colorHue()}, 100%, 50%))`
    );

    constructor() {
        registerLinkPlaceholderBlot();
        registerDataTagBlot();
        registerSpanStyleFormats();

        effect(() => {
            this._dataTagContext?.();
            this._linkController.links();
            this._syncDataTagDisplay();
            if (this.quill) {
                setTimeout(() => this._refreshTagValidity(), 0);
            }
        });

        effect(() => {
            const raw = this.value();

            const nextValue =
                raw == null || raw === ''
                    ? ''
                    : typeof raw === 'string'
                      ? raw
                      : (JSON.stringify(raw) ?? '');
            // Evitar re-aplicar quando a mudança veio do próprio editor (evita cursor pular)
            if (nextValue === this.lastEmittedValue) {
                return;
            }
            if (nextValue === this.valueEditor) {
                if (
                    !this.limitCharacters() ||
                    this._getTextLength(nextValue) <= this.limitCharacters()
                ) {
                    this.lastValidValue = nextValue;
                }
                return;
            }

            this.valueEditor = nextValue;
            if (
                !this.limitCharacters() ||
                this._getTextLength(this.valueEditor) <= this.limitCharacters()
            ) {
                this.lastValidValue = this.valueEditor;
            }

            setTimeout(() => this._applyContent(), 0);
        });
    }

    ngOnInit(): void {
        this._syncDataTagDisplay();
        this._setEditorModules();
        this._setupWindowListeners();
        this._langChangeSub = this._translate.onLangChange.subscribe(() => {
            this._langVersion.update(v => v + 1);
            this._syncDataTagDisplay();
            if (this.quill && this.valueEditor?.trim()) {
                setTimeout(() => this._applyContent(), 0);
            }
        });
    }

    ngOnDestroy(): void {
        this._langChangeSub?.unsubscribe();
        this._removeWindowListeners();
        this._cleanupQuillListeners();
        this._cleanupLinkTooltipListeners();
        this._stopPaletteDrag();
    }

    /** Rótulo traduzido para autocomplete e exibição. */
    public getLinkLabel(link: LinkEntity): string {
        return this._getDataTagLabel(this._getLinkTagValue(link));
    }

    private _syncDataTagDisplay(): void {
        syncDataTagDisplay({
            translate: this._translate,
            links: this._linkController.links(),
            contextSignal: this._dataTagContext ?? undefined,
            findLinkByTag: tag => this._findLinkByTag(tag),
        });
    }

    private _refreshTagValidity(): void {
        if (!this.quill) return;
        refreshDataTagValidityInRoot(this.quill.root);
    }

    private _findLinkByTag(tag: string): LinkEntity | undefined {
        return this._linkController
            .links()
            .find(l => this._normalizeLinkValue(l.value ?? l.tagName ?? '') === tag);
    }

    private _getDataTagLabel(tag: string): string {
        const link = this._findLinkByTag(tag);
        return getDataTagLabel(this._translate, tag, link?.description || link?.tagName || link?.name);
    }

    private _getDataTagHint(tag: string): string | undefined {
        return getDataTagHint(this._translate, tag) ?? this._findLinkByTag(tag)?.description;
    }

    /** Ícone da categoria do link, exibido na lista de autocomplete. */
    public getLinkIcon(link: LinkEntity): string {
        return getLinkCategoryIcon(link);
    }

    private _getLinkCategoryLabel(link: LinkEntity): string {
        if (link.categoryDescription?.trim()) return link.categoryDescription;
        const code = link.category?.trim();
        if (!code) return this._translate.instant('shared.editor.categoryOther');
        const key = `shared.editor.linkCategories.${code}`;
        const translated = this._translate.instant(key);
        return translated !== key ? translated : code;
    }

    /**
     * Cria o editor Quill e aplica o conteúdo.
     */
    public onEditorCreated(editor: Quill): void {
        this.quill = editor;
        // Evita que tradutores automáticos alterem o DOM interno do editor.
        this.quill.root.setAttribute('translate', 'no');
        this.quill.root.classList.add('notranslate');
        this.quill.root.setAttribute('data-gramm', 'false');
        this.quill.root.setAttribute('data-gramm_editor', 'false');
        this.quill.root.setAttribute('data-enable-grammarly', 'false');
        this._setupPasteColorStripping();
        this._syncDataTagDisplay();
        this._applyContent();
        this._refreshTagValidity();
        this._setupAutocompleteListeners();
        this._setupLinkTooltipListeners();
    }

    /**
     * Remove cor e cor de fundo de qualquer conteúdo colado, para o texto
     * herdar a cor padrão do documento em vez de manter as cores da origem.
     * Não afeta cores aplicadas manualmente via color picker depois da colagem.
     */
    private _setupPasteColorStripping(): void {
        if (!this.quill) return;

        this.quill.clipboard.addMatcher(Node.ELEMENT_NODE, (_node, delta) => {
            delta.ops.forEach(op => {
                if (op.attributes) {
                    delete op.attributes['color'];
                    delete op.attributes['background'];
                }
            });
            return delta;
        });
    }

    /**
     * Foca o editor e posiciona o cursor no final do conteúdo.
     *
     * Adiado com setTimeout porque o conteúdo externo também é aplicado com
     * setTimeout (_applyContent): agendar o foco depois garante que ele rode com
     * o conteúdo novo — sem isso, getLength() media o conteúdo antigo e o cursor
     * ficava no início em textos grandes.
     */
    public focus(): void {
        if (!this.quill || this.disabled()) return;

        setTimeout(() => {
            if (!this.quill || this.disabled()) return;

            // Sem rolar a página: quem traz o campo para a tela é o editor (e só quando ele
            // está fora dela). O focus padrão do Quill rolava na hora, e a rolagem suave vinha
            // em seguida: dois pulos.
            this.quill.focus({ preventScroll: true });
            this.quill.setSelection(this.quill.getLength(), 0, 'silent');

            // O .ql-editor tem max-height com scroll próprio: rola até o fim
            // para a última linha (onde o cursor está) ficar visível.
            this.quill.root.scrollTop = this.quill.root.scrollHeight;
        }, 0);
    }

    /**
     * Aplica um formato do Quill (bold, italic, underline, color...) à seleção atual, ou ao
     * cursor quando não há seleção (o que for digitado a seguir sai formatado). É por aqui
     * que a barra de formatação do editor age no texto em foco.
     */
    public format(name: string, value: unknown): void {
        if (!this.quill || this.disabled()) return;

        this.quill.format(name, value as never, 'user');
        this._emitFormat();
    }

    /**
     * Tira toda a formatação do trecho selecionado (negrito, cor, tamanho, fonte, lista...).
     * Sem trecho, limpa os formatos pendentes do cursor, para o que vier a seguir sair limpo.
     */
    public clearFormat(): void {
        if (!this.quill || this.disabled()) return;

        const range = this.quill.getSelection();
        if (range && range.length > 0) {
            this.quill.removeFormat(range.index, range.length, 'user');
        } else {
            for (const name of ['bold', 'italic', 'underline', 'strike', 'color', 'background', 'size', 'font', 'link', 'header'])
                this.quill.format(name, false, 'user');
        }
        this._emitFormat();
    }

    /**
     * Aplica um formato de LINHA (cabeçalho, lista) a todas as linhas do texto. É como a barra
     * transforma um título de várias linhas em texto com a classe de cada linha.
     */
    public formatAllLines(name: string, value: unknown): void {
        if (!this.quill || this.disabled()) return;

        this.quill.formatLine(0, this.quill.getLength(), name, value as never, 'user');
        this._emitFormat();
    }

    /** A seleção atual cobre algum trecho (não é só o cursor)? */
    public hasSelection(): boolean {
        const range = this.quill?.getSelection();
        return !!range && range.length > 0;
    }

    /**
     * Aplica formatos de TRECHO (tamanho, fonte, cor...) ao texto inteiro das linhas que a
     * seleção cobre (a linha do cursor, sem seleção). `false` tira o formato. Sem foco, o
     * Quill volta ao foco e usa a última seleção.
     */
    public formatSelectedLinesText(formats: Record<string, unknown>): void {
        if (!this.quill || this.disabled()) return;

        const range = this.quill.getSelection(true);
        if (!range) return;
        const lines = this.quill.getLines(range.index, Math.max(range.length, 1));
        if (lines.length === 0) return;

        const start = this.quill.getIndex(lines[0]);
        const last = lines[lines.length - 1];
        const end = this.quill.getIndex(last) + last.length();
        this.quill.formatText(start, end - start, formats as never, 'user');
        this._emitFormat();
    }

    /** Quantas linhas (blocos) o texto tem. */
    public lineCount(): number {
        return this.quill?.getLines().length ?? 0;
    }

    /** Quantas linhas a seleção atual cobre (1 com só o cursor; 0 sem seleção no editor). */
    public selectedLineCount(): number {
        const range = this.quill?.getSelection();
        if (!this.quill || !range) return 0;
        return this.quill.getLines(range.index, Math.max(range.length, 1)).length;
    }

    private _emitFormat(): void {
        if (!this.quill) return;
        try {
            this.formatChange.emit({
                format: this.quill.getFormat() as Record<string, unknown>,
                hasSelection: this.hasSelection(),
            });
        } catch {
            this.formatChange.emit({ format: {}, hasSelection: false });
        }
    }

    /**
     * Chamado a cada mudança de conteúdo. Bloqueia digitação acima do limite.
     */
    public onContentChange(): void {
        const currentContent =
            this.quill != null ? JSON.stringify(this.quill.getContents()) : this.valueEditor;
        this.valueEditor = currentContent;

        const limit = this.limitCharacters();
        const cleanContent = this._getCleanContent();
        if (!limit) {
            this.lastValidValue = currentContent;
            this.lastEmittedValue = cleanContent;
            this.change.emit(cleanContent);
            return;
        }

        const len = this._getTextLength(currentContent);
        if (len > limit) {
            this.valueEditor = this.lastValidValue;
            this._applyContent();
            return;
        }

        this.lastValidValue = currentContent;
        this.lastEmittedValue = cleanContent;
        this.change.emit(cleanContent);
    }

    public openColorPicker(mode: ColorPickerMode): void {
        if (!this.quill || this.disabled()) return;

        const range = this.quill.getSelection();
        if (range) {
            this.lastSelectionRange = range as QuillRange;
        }

        this.colorPickerMode.set(mode);
        this.showColorPicker.set(true);
        setTimeout(() => this.updateColorPickerPosition(), 0);
    }

    public closeColorPicker(): void {
        this.showColorPicker.set(false);
    }

    public selectQuickColor(hex: string): void {
        this.setSelectedColor(hex);
        this.applySelectedColor();
    }

    public onColorPalettePointer(event: MouseEvent): void {
        event.preventDefault();

        const area = event.currentTarget as HTMLElement;
        // Atualiza a cor já no clique inicial e inicia o arraste
        this._updateColorFromPalette(area, event);

        this._paletteDragMoveHandler = (moveEvent: MouseEvent) => {
            this._updateColorFromPalette(area, moveEvent);
        };
        this._paletteDragUpHandler = () => this._stopPaletteDrag();

        window.addEventListener('mousemove', this._paletteDragMoveHandler);
        window.addEventListener('mouseup', this._paletteDragUpHandler);
    }

    /** Calcula saturação/brilho a partir da posição do ponteiro e aplica a cor. */
    private _updateColorFromPalette(area: HTMLElement, event: MouseEvent): void {
        const rect = area.getBoundingClientRect();
        const x = Math.min(Math.max(event.clientX - rect.left, 0), rect.width);
        const y = Math.min(Math.max(event.clientY - rect.top, 0), rect.height);

        this.colorSaturation.set(Math.round((x / rect.width) * 100));
        this.colorBrightness.set(Math.round(100 - (y / rect.height) * 100));
        this.selectedColorHex.set(
            this.hsvToHex(this.colorHue(), this.colorSaturation(), this.colorBrightness())
        );
        this.applySelectedColor();
    }

    private _stopPaletteDrag(): void {
        if (this._paletteDragMoveHandler) {
            window.removeEventListener('mousemove', this._paletteDragMoveHandler);
            this._paletteDragMoveHandler = undefined;
        }
        if (this._paletteDragUpHandler) {
            window.removeEventListener('mouseup', this._paletteDragUpHandler);
            this._paletteDragUpHandler = undefined;
        }
    }

    public onHueChange(value: string): void {
        this.colorHue.set(Number(value));
        this.selectedColorHex.set(
            this.hsvToHex(this.colorHue(), this.colorSaturation(), this.colorBrightness())
        );
        this.applySelectedColor();
    }

    public onHexChange(value: string): void {
        const normalized = this.normalizeHex(value);
        this.selectedColorHex.set(value);
        if (!normalized) return;

        this.setSelectedColor(normalized);
        this.applySelectedColor();
    }

    private setSelectedColor(hex: string): void {
        const normalized = this.normalizeHex(hex);
        if (!normalized) return;

        const hsv = this.hexToHsv(normalized);
        this.colorHue.set(hsv.h);
        this.colorSaturation.set(hsv.s);
        this.colorBrightness.set(hsv.v);
        this.selectedColorHex.set(normalized);
    }

    private applySelectedColor(): void {
        if (!this.quill) return;

        const range = this.lastSelectionRange ?? (this.quill.getSelection() as QuillRange | null);
        if (range) {
            this.quill.setSelection(range.index, range.length, 'silent');
        }

        this.quill.format(this.colorPickerMode(), this.selectedColorHex(), 'user');
        this.valueEditor = JSON.stringify(this.quill.getContents());
        this.onContentChange();
        this._updateColorButtonsActive();
    }

    /**
     * Remove a cor (de texto ou fundo, conforme o modo do seletor) da seleção.
     */
    public removeColor(): void {
        if (!this.quill) return;

        const range = this.lastSelectionRange ?? (this.quill.getSelection() as QuillRange | null);
        if (range) {
            this.quill.setSelection(range.index, range.length, 'silent');
        }

        this.quill.format(this.colorPickerMode(), false, 'user');
        this.valueEditor = JSON.stringify(this.quill.getContents());
        this.onContentChange();
        this._updateColorButtonsActive();
        this.closeColorPicker();
    }

    /**
     * Retorna o conteúdo sem atributos de formatação de link (para não alterar o salvo).
     */
    private _getCleanContent(): string {
        if (!this.quill) return this.valueEditor;
        try {
            const delta = this.quill.getContents();
            const ops = delta.ops as DeltaOps;
            if (!Array.isArray(ops)) return this.valueEditor;
            return JSON.stringify({ ops: this._opsForSave(ops) });
        } catch {
            return this.valueEditor;
        }
    }

    /**
     * Normaliza o Delta para persistência: tags como { dataTag }, sem atributos de exibição.
     */
    private _opsForSave(ops: DeltaOps): DeltaOps {
        const knownTags = new Set(
            this._linkController
                .links()
                .map(l => this._normalizeLinkValue(l.value ?? l.tagName ?? ''))
                .filter(Boolean)
        );
        const result: DeltaOps = [];

        for (const op of ops) {
            if (isDataTagInsert(op.insert)) {
                const tag = getDataTagFromInsert(op.insert)!;
                result.push({ insert: { [DATA_TAG_BLOT_NAME]: tag } });
                continue;
            }

            if (typeof op.insert !== 'string') {
                result.push(op);
                continue;
            }

            const { [DATA_LINK_ATTR]: _, ...restAttrs } = (op.attributes ?? {}) as Record<
                string,
                unknown
            >;
            const attrs = Object.keys(restAttrs).length ? restAttrs : undefined;
            const text = op.insert;
            const regex = /\$?([A-Za-z0-9_]+)/g;
            let lastIndex = 0;
            let match: RegExpExecArray | null;

            while ((match = regex.exec(text)) !== null) {
                const token = match[0];
                const normalized = this._normalizeLinkValue(match[1]);
                const isLegacyLink = token.startsWith('$') && knownTags.has(normalized);
                const isBareTag = !token.startsWith('$') && knownTags.has(normalized);

                if (match.index > lastIndex) {
                    result.push({ insert: text.slice(lastIndex, match.index), attributes: attrs });
                }

                if (isLegacyLink || isBareTag) {
                    result.push({ insert: { [DATA_TAG_BLOT_NAME]: normalized } });
                } else {
                    result.push({ insert: token, attributes: attrs });
                }
                lastIndex = regex.lastIndex;
            }

            if (lastIndex < text.length) {
                result.push({ insert: text.slice(lastIndex), attributes: attrs });
            }
        }

        return result.length ? result : ops;
    }

    /**
     * Aplica o Delta atual ao editor, adicionando destaque visual nos links.
     */
    private _applyContent(): void {
        if (!this.quill) return;

        if (!this.valueEditor?.trim()) {
            this.quill.deleteText(0, this.quill.getLength(), 'silent');
            (this.quill.getModule('history') as { clear?: () => void })?.clear?.();
            return;
        }

        try {
            const delta = JSON.parse(this.valueEditor) as { ops?: DeltaOps };
            const ops = delta?.ops;
            if (!Array.isArray(ops) || ops.length === 0) return;

            const enriched = this._addLinksDisplayFormat(ops);
            this.quill.updateContents(
                [{ delete: this.quill.getLength() }, ...enriched] as never,
                'silent'
            );
            (this.quill.getModule('history') as { clear?: () => void })?.clear?.();
            this._refreshTagValidity();
        } catch {
            // ignore
        }
    }

    /**
     * Adiciona atributo de destaque para links no Delta (apenas para exibição).
     */
    private _addLinksDisplayFormat(ops: DeltaOps): DeltaOps {
        const links = this._linkController.links();
        const knownValues = new Set(
            links.map(l => this._normalizeLinkValue(l.value ?? l.tagName ?? ''))
        );
        const descMap = new Map(
            links.map(l => [this._normalizeLinkValue(l.value ?? l.tagName ?? ''), l.description ?? ''])
        );
        const result: DeltaOps = [];

        for (const op of ops) {
            if (isDataTagInsert(op.insert)) {
                const tag = getDataTagFromInsert(op.insert)!;
                ensureDataTagMeta(tag);
                result.push({ insert: { [DATA_TAG_BLOT_NAME]: tag } });
                continue;
            }

            if (typeof op.insert !== 'string') {
                result.push(op);
                continue;
            }
            const text = op.insert;
            const regex = /\$?[A-Za-z0-9_]+/g;
            let lastIndex = 0;
            let match: RegExpExecArray | null;

            while ((match = regex.exec(text)) !== null) {
                const token = match[0];
                const normalized = this._normalizeLinkValue(token);
                const isLink = token.startsWith('$') && knownValues.has(normalized);

                if (match.index > lastIndex) {
                    result.push({
                        insert: text.slice(lastIndex, match.index),
                        attributes: op.attributes,
                    });
                }
                result.push({
                    insert: isLink ? `$${normalized}` : token,
                    attributes: isLink
                        ? {
                              ...(op.attributes ?? {}),
                              [DATA_LINK_ATTR]: encodeDataLinkFormat(
                                  normalized,
                                  descMap.get(normalized)
                              ),
                          }
                        : op.attributes,
                });
                lastIndex = regex.lastIndex;
            }
            if (lastIndex < text.length) {
                result.push({ insert: text.slice(lastIndex), attributes: op.attributes });
            }
        }
        return result.length ? result : ops;
    }

    private _normalizeLinkValue(v: string): string {
        return v.replace(/^\$/, '').trim();
    }

    private _getLinkDescription(tagKey: string): string {
        return this._getDataTagHint(tagKey) ?? this._getDataTagLabel(tagKey);
    }

    /**
     * Aplica destaque visual aos links digitados manualmente (ex: $CLIENT_NAME).
     */
    private _applyLinksHighlightOnType(): void {
        if (!this.quill || !this.enableLinkAutocomplete()) return;

        const knownValues = new Set(
            this._linkController
                .links()
                .map(l => this._normalizeLinkValue(l.value ?? l.tagName ?? ''))
        );
        if (knownValues.size === 0) return;

        const fullText = this.quill.getText(0, this.quill.getLength());
        const regex = /\$?[A-Za-z0-9_]+/g;
        let match: RegExpExecArray | null;
        const toReplace: Array<{
            index: number;
            length: number;
            canonical: string;
            normalized: string;
        }> = [];
        const toFormat: Array<{ index: number; length: number; normalized: string }> = [];
        const toRemoveFormat: Array<{ index: number; length: number }> = [];

        while ((match = regex.exec(fullText)) !== null) {
            const token = match[0];
            const normalized = this._normalizeLinkValue(token);
            const index = match.index;
            const length = token.length;

            if (!token.startsWith('$')) {
                // Token sem $: remover formato se tiver (evita "das", "partes" virarem links)
                const currentFormat = this.quill.getFormat(index, length) as Record<
                    string,
                    unknown
                >;
                if (currentFormat[DATA_LINK_ATTR]) {
                    toRemoveFormat.push({ index, length });
                }
                continue;
            }

            if (knownValues.has(normalized)) {
                const canonical = `$${normalized}`;
                if (token !== canonical) {
                    toReplace.push({ index, length, canonical, normalized });
                } else {
                    toFormat.push({ index, length, normalized });
                }
            } else {
                const currentFormat = this.quill.getFormat(index, length) as Record<
                    string,
                    unknown
                >;
                if (currentFormat[DATA_LINK_ATTR]) {
                    toRemoveFormat.push({ index, length });
                }
            }
        }

        // Calcular delta acumulado para cada índice (substituições que alteram tamanho)
        const sortedReplaces = [...toReplace].sort((a, b) => a.index - b.index);
        const getDeltaBefore = (idx: number) =>
            sortedReplaces
                .filter(r => r.index < idx)
                .reduce((sum, r) => sum + r.canonical.length - r.length, 0);

        const formatValue = (n: string) => encodeDataLinkFormat(n, this._getLinkDescription(n));
        for (const { index, length, canonical, normalized } of sortedReplaces) {
            const adjIndex = index + getDeltaBefore(index);
            this.quill.deleteText(adjIndex, length, 'silent');
            this.quill.insertText(
                adjIndex,
                canonical,
                { [DATA_LINK_ATTR]: formatValue(normalized) },
                'silent'
            );
        }
        for (const { index, length, normalized } of toFormat) {
            this.quill.formatText(
                index + getDeltaBefore(index),
                length,
                DATA_LINK_ATTR,
                formatValue(normalized),
                'silent'
            );
        }
        for (const { index, length } of toRemoveFormat) {
            this.quill.formatText(
                index + getDeltaBefore(index),
                length,
                DATA_LINK_ATTR,
                false,
                'silent'
            );
        }

        // Remove formato do cursor para não "vazar" no próximo texto digitado
        const range = this.quill.getSelection();
        if (range && range.length === 0) {
            this.quill.format(DATA_LINK_ATTR, false, 'silent');
        }
    }

    /**
     * Configura listeners para autocomplete (@).
     */
    private _setupAutocompleteListeners(): void {
        if (!this.quill || !this.enableLinkAutocomplete()) return;

        this.textChangeHandler = (_delta: unknown, _old: unknown, source: string) => {
            if (source === 'user') {
                setTimeout(() => {
                    this._onTextChange();
                    this._applyLinksHighlightOnType();
                    this._refreshTagValidity();
                }, 0);
            }
        };
        this.quill.on('text-change', this.textChangeHandler);

        this.selectionChangeHandler = (range: unknown, _old: unknown, source: string) => {
            if (source !== 'user') return;
            if (!range) {
                this._closeAutocomplete();
                return;
            }
            this._onTextChange();
        };
        this.quill.on('selection-change', this.selectionChangeHandler);
    }

    private _cleanupQuillListeners(): void {
        if (!this.quill) return;
        if (this.textChangeHandler) {
            this.quill.off('text-change', this.textChangeHandler);
        }
        if (this.selectionChangeHandler) {
            this.quill.off('selection-change', this.selectionChangeHandler);
        }
    }

    private _setupLinkTooltipListeners(): void {
        if (!this.quill || !this.enableLinkAutocomplete()) return;

        this.linkTooltipMouseEnter = (e: MouseEvent) => {
            const linkEl = (e.target as HTMLElement).closest(
                '.ql-data-link, .ql-data-tag'
            ) as HTMLElement | null;
            if (!linkEl) {
                if (this.linkTooltipShowTimeout) {
                    clearTimeout(this.linkTooltipShowTimeout);
                    this.linkTooltipShowTimeout = null;
                }
                this._hideLinkTooltip();
                return;
            }
            const tagKey =
                linkEl.getAttribute('data-tag') ?? linkEl.getAttribute('data-value') ?? '';
            const isEmpty =
                linkEl.classList.contains(DATA_TAG_EMPTY_CLASS) ||
                linkEl.classList.contains(DATA_LINK_EMPTY_CLASS);
            const desc = isEmpty
                ? this._translate.instant('shared.editor.dataTagEmpty')
                : (linkEl.getAttribute(DATA_LINK_DESC_ATTR) ?? this._getLinkDescription(tagKey));
            if (!desc?.trim()) return;
            if (this.linkTooltipHideTimeout) {
                clearTimeout(this.linkTooltipHideTimeout);
                this.linkTooltipHideTimeout = null;
            }
            if (this.linkTooltipShowTimeout) clearTimeout(this.linkTooltipShowTimeout);
            this.linkTooltipShowTimeout = setTimeout(
                () => this._showLinkTooltip(linkEl, desc),
                300
            );
        };

        this.linkTooltipMouseLeave = (e: MouseEvent) => {
            const from = (e.target as HTMLElement).closest('.ql-data-link, .ql-data-tag');
            const to = (e.relatedTarget as HTMLElement)?.closest?.('.ql-data-link, .ql-data-tag');
            if (from && !to) {
                if (this.linkTooltipShowTimeout) {
                    clearTimeout(this.linkTooltipShowTimeout);
                    this.linkTooltipShowTimeout = null;
                }
                this.linkTooltipHideTimeout = setTimeout(() => this._hideLinkTooltip(), 100);
            }
        };

        this.quill.root.addEventListener('mouseover', this.linkTooltipMouseEnter, true);
        this.quill.root.addEventListener('mouseout', this.linkTooltipMouseLeave, true);
    }

    private _cleanupLinkTooltipListeners(): void {
        if (this.quill && this.linkTooltipMouseEnter && this.linkTooltipMouseLeave) {
            this.quill.root.removeEventListener('mouseover', this.linkTooltipMouseEnter, true);
            this.quill.root.removeEventListener('mouseout', this.linkTooltipMouseLeave, true);
        }
        if (this.linkTooltipShowTimeout) clearTimeout(this.linkTooltipShowTimeout);
        if (this.linkTooltipHideTimeout) clearTimeout(this.linkTooltipHideTimeout);
        this._hideLinkTooltip();
    }

    private _showLinkTooltip(hostEl: HTMLElement, text: string): void {
        this._hideLinkTooltip();
        this.linkTooltipElement = document.createElement('div');
        this.linkTooltipElement.className = 'ra-tooltip ra-tooltip--top';
        Object.assign(this.linkTooltipElement.style, {
            position: 'fixed',
            zIndex: '9999',
            padding: '0.625rem 0.875rem',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            lineHeight: '1.4',
            fontWeight: '500',
            maxWidth: '15.625rem',
            wordWrap: 'break-word',
            pointerEvents: 'none',
            opacity: '0',
            visibility: 'hidden',
            transform: 'scale(0.9)',
            transition: 'all 0.15s ease-out',
            backgroundColor: '#1f2937',
            color: '#ffffff',
            border: '1px solid #374151',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
        });
        this.linkTooltipElement.textContent = text;
        this.linkTooltipElement.setAttribute('role', 'tooltip');
        document.body.appendChild(this.linkTooltipElement);

        const hostRect = hostEl.getBoundingClientRect();
        const ttRect = this.linkTooltipElement.getBoundingClientRect();
        let top = hostRect.top - ttRect.height - 8;
        let left = hostRect.left + hostRect.width / 2 - ttRect.width / 2;
        if (top < 8) top = 8;
        if (left < 8) left = 8;
        if (left + ttRect.width > window.innerWidth - 8)
            left = window.innerWidth - ttRect.width - 8;
        this.linkTooltipElement.style.top = `${top}px`;
        this.linkTooltipElement.style.left = `${left}px`;
        this.linkTooltipElement.classList.add('ra-tooltip--visible');
        this.linkTooltipElement.style.opacity = '1';
        this.linkTooltipElement.style.visibility = 'visible';
        this.linkTooltipElement.style.transform = 'scale(1)';
    }

    private _hideLinkTooltip(): void {
        if (this.linkTooltipElement?.parentNode) {
            this.linkTooltipElement.parentNode.removeChild(this.linkTooltipElement);
        }
        this.linkTooltipElement = null;
    }

    private _onTextChange(): void {
        if (this.justInsertedLink) {
            this.justInsertedLink = false;
            return;
        }

        const range = this.quill?.getSelection();
        if (!range) return;

        const textBefore = this.quill!.getText(0, range.index);
        const match = textBefore.match(/@([^\s]*)$/);

        if (!match) {
            this._closeAutocomplete();
            return;
        }

        const searchTerm = match[1];
        const fullMatch = match[0];

        // Não abrir se o cursor estiver dentro de um link completo
        const beforeCursor = textBefore.slice(0, range.index - fullMatch.length);
        const linkRegex = /\$?[A-Za-z0-9_]+$/;
        if (linkRegex.test(beforeCursor)) {
            const lastLink = beforeCursor.match(/\$?([A-Za-z0-9_]+)$/);
            if (lastLink) {
                const linkVal = this._normalizeLinkValue(lastLink[1]);
                const isKnown = this._linkController
                    .links()
                    .some(l => this._normalizeLinkValue(l.value ?? l.tagName ?? '') === linkVal);
                if (isKnown) {
                    this._closeAutocomplete();
                    return;
                }
            }
        }

        this.autocompleteSearch.set(searchTerm);
        this.autocompleteIndex.set(-1);
        this.showAutocompleteList.set(true);
        this._updateAutocompletePosition();
    }

    private _updateAutocompletePosition(): void {
        if (!this.quill) return;
        const range = this.quill.getSelection();
        if (!range) return;
        const bounds = this.quill.getBounds(range.index);
        const editorRect = this.quill.root.getBoundingClientRect();
        const hostRect = (this._host.nativeElement as HTMLElement).getBoundingClientRect();
        // Posição acima do cursor (estilo WhatsApp)
        this.autocompletePosition.set({
            top: bounds.top + editorRect.top - hostRect.top,
            left: bounds.left + editorRect.left - hostRect.left,
        });
    }

    private _closeAutocomplete(): void {
        this.showAutocompleteList.set(false);
        this.autocompleteSearch.set('');
        this.autocompleteIndex.set(-1);
    }

    private _handleArrowDown(): boolean {
        if (!this.showAutocompleteList()) return true; // Deixar Quill processar (mover cursor)
        const list = this.displayLinks();
        // Nada selecionado ainda: a primeira seta pra baixo foca o 1º item
        this.autocompleteIndex.set(
            Math.min(this.autocompleteIndex() + 1, Math.max(0, list.length - 1))
        );
        setTimeout(() => this._scrollSelectedIntoView(), 0);
        return false; // Consumir evento
    }

    private _handleArrowUp(): boolean {
        if (!this.showAutocompleteList()) return true; // Deixar Quill processar (mover cursor)
        this.autocompleteIndex.set(Math.max(this.autocompleteIndex() - 1, 0));
        setTimeout(() => this._scrollSelectedIntoView(), 0);
        return false; // Consumir evento
    }

    /**
     * Rola apenas o container da lista (não a página) até o item selecionado,
     * sem animação: com a seta pressionada, animações concorrentes se atrapalham.
     */
    private _scrollSelectedIntoView(): void {
        const host = this._host.nativeElement as HTMLElement;
        const list = host.querySelector<HTMLElement>('.autocomplete-list');
        const selected = host.querySelector<HTMLElement>('.autocomplete-item.selected');
        if (!list || !selected) return;

        const itemTop = selected.offsetTop;
        const itemBottom = itemTop + selected.offsetHeight;
        const viewTop = list.scrollTop;
        const viewBottom = viewTop + list.clientHeight;

        if (itemTop < viewTop) {
            list.scrollTop = itemTop;
        } else if (itemBottom > viewBottom) {
            list.scrollTop = itemBottom - list.clientHeight;
        }
    }

    private _handleEnter(): boolean {
        if (!this.showAutocompleteList()) {
            if (this.submitOnEnter()) {
                const cleanContent = this._getCleanContent();
                this.lastEmittedValue = cleanContent;
                this.submit.emit(cleanContent);
                return false;
            }

            return true; // Deixar Quill processar (nova linha)
        }
        const list = this.displayLinks();
        // Sem item focado ainda, Enter confirma o primeiro da lista
        const idx = this.autocompleteIndex() < 0 ? 0 : this.autocompleteIndex();
        if (list[idx]) {
            this._insertLink(list[idx]);
            return false; // Consumir evento (evitar nova linha)
        }
        return false;
    }

    /**
     * Cmd/Ctrl+Enter: envia sempre (ignora `submitOnEnter`). Com o autocomplete
     * aberto, comporta-se como Enter (seleciona o link em foco) em vez de enviar.
     */
    private _handleSubmitShortcut(): boolean {
        if (this.showAutocompleteList()) {
            return this._handleEnter();
        }

        const cleanContent = this._getCleanContent();
        this.lastEmittedValue = cleanContent;
        this.submit.emit(cleanContent);
        return false; // Consome o evento (não insere nova linha)
    }

    private _handleEscape(): boolean {
        if (!this.showAutocompleteList()) {
            // Inline: Escape é "terminei de editar" (o blur salva o que falta).
            if (this.inline()) {
                this.quill?.blur();
                return false;
            }
            return true; // Deixar Quill processar
        }
        this._closeAutocomplete();
        return false; // Consumir evento
    }

    public selectLink(link: LinkEntity): void {
        this._insertLink(link);
    }

    private _insertLink(link: LinkEntity): void {
        if (!this.quill) return;
        const range = this.quill.getSelection();
        if (!range) return;

        const textBefore = this.quill.getText(0, range.index);
        const match = textBefore.match(/@([^\s]*)$/);
        if (!match) return;

        const tagValue = this._getLinkTagValue(link);
        const insertLength = match[0].length;
        const insertIndex = range.index - insertLength;

        this.quill.deleteText(insertIndex, insertLength, 'user');
        this.quill.insertEmbed(insertIndex, DATA_TAG_BLOT_NAME, tagValue, 'user');
        const cursorPos = insertIndex + 1;
        this.quill.setSelection(cursorPos, 0);
        this.quill.format(DATA_LINK_ATTR, false, 'silent'); // Remove formato do cursor para não "vazar" no próximo texto

        this.valueEditor = JSON.stringify(this.quill.getContents());
        this.lastValidValue = this.valueEditor;
        const cleanContent = this._getCleanContent();
        this.lastEmittedValue = cleanContent;
        this.change.emit(cleanContent);

        this.justInsertedLink = true;
        this._closeAutocomplete();
        setTimeout(() => this._refreshTagValidity(), 0);
    }

    private _getLinkTagValue(link: LinkEntity): string {
        return this._normalizeLinkValue(link.value ?? link.tagName ?? link.name ?? '');
    }

    /**
     * Quantidade de caracteres de texto do Delta.
     */
    public getTextLength = () => this._getTextLength(this.valueEditor);

    /**
     * Retorna a quantidade de caracteres. Aceita Delta JSON ({ ops: [...] }) ou texto puro.
     */
    private _getTextLength(deltaJson: string): number {
        if (!deltaJson?.trim()) return 0;
        try {
            const parsed = JSON.parse(deltaJson) as { ops?: Array<{ insert: string | object }> };
            if (parsed?.ops && Array.isArray(parsed.ops)) {
                let len = 0;
                for (const op of parsed.ops) {
                    if (typeof op.insert === 'string') len += op.insert.length;
                    else if (op.insert && typeof op.insert === 'object') len += 1;
                }
                return len;
            }
        } catch {
            // valor já é texto puro, não JSON
        }
        return deltaJson.length;
    }

    /**
     * Configura os módulos do editor Quill.
     */
    private _setEditorModules(): void {
        const keyboardBindings: Record<
            string,
            {
                key: number | string;
                metaKey?: boolean;
                ctrlKey?: boolean;
                handler: (range: unknown, context: unknown) => boolean;
            }
        > = {
            autocompleteEnter: {
                key: 'Enter',
                handler: () => this._handleEnter(),
            },
            // Cmd+Enter (Mac) e Ctrl+Enter (Windows/Linux) sempre enviam,
            // independente de `submitOnEnter`. Bindings separados porque cada
            // um exige que os demais modificadores estejam soltos.
            submitCmdEnter: {
                key: 'Enter',
                metaKey: true,
                handler: () => this._handleSubmitShortcut(),
            },
            submitCtrlEnter: {
                key: 'Enter',
                ctrlKey: true,
                handler: () => this._handleSubmitShortcut(),
            },
            autocompleteArrowDown: {
                key: 'ArrowDown',
                handler: () => this._handleArrowDown(),
            },
            autocompleteArrowUp: {
                key: 'ArrowUp',
                handler: () => this._handleArrowUp(),
            },
            autocompleteEscape: {
                key: 'Escape',
                handler: () => this._handleEscape(),
            },
        };

        if (!this.showToolbar()) {
            this.modules = {
                toolbar: false,
                keyboard: { bindings: keyboardBindings },
            };
            return;
        }

        this.modules = {
            toolbar: {
                container: [
                    ['bold', 'italic', 'underline'],
                    ['colorPicker', 'backgroundPicker'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                ],
                handlers: {
                    colorPicker: () => this.openColorPicker('color'),
                    backgroundPicker: () => this.openColorPicker('background'),
                },
            },
            keyboard: { bindings: keyboardBindings },
        };
    }

    /**
     * Emite foco com base no selection-change do Quill.
     */
    public onSelectionChanged(event: { range?: unknown }): void {
        if (this.disabled()) return;

        const isFocused = !!event?.range;
        if (isFocused) {
            this._updateColorButtonsActive();
            this._emitFormat();
        }

        if (isFocused && !this.hasFocus()) {
            this.lastSelectionRange = event.range as QuillRange;
            this.hasFocus.set(true);
            this.onFocus.emit();
            return;
        }

        if (isFocused) {
            this.lastSelectionRange = event.range as QuillRange;
        }

        if (!isFocused && this.hasFocus()) {
            this._handleLeave();
        }
    }

    /**
     * Marca os botões de cor/fundo (customizados) como ativos quando a seleção
     * atual possui essa formatação — o Quill só faz isso nativamente para
     * bold/itálico/sublinhado/listas.
     */
    private _updateColorButtonsActive(): void {
        if (!this.quill) return;

        const toolbar = this.quill.getModule('toolbar') as { container?: HTMLElement } | undefined;
        const container = toolbar?.container;
        if (!container) return;

        const format = this.quill.getFormat() as Record<string, unknown>;
        container.querySelector('.ql-colorPicker')?.classList.toggle('ql-active', !!format['color']);
        container
            .querySelector('.ql-backgroundPicker')
            ?.classList.toggle('ql-active', !!format['background']);
    }

    private normalizeHex(value: string): string | null {
        const hex = value.trim().replace(/^#/, '').toUpperCase();
        if (!/^[0-9A-F]{6}$/.test(hex)) return null;

        return `#${hex}`;
    }

    private hsvToHex(h: number, s: number, v: number): string {
        const saturation = s / 100;
        const brightness = v / 100;
        const chroma = brightness * saturation;
        const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
        const m = brightness - chroma;
        let r = 0;
        let g = 0;
        let b = 0;

        if (h < 60) [r, g, b] = [chroma, x, 0];
        else if (h < 120) [r, g, b] = [x, chroma, 0];
        else if (h < 180) [r, g, b] = [0, chroma, x];
        else if (h < 240) [r, g, b] = [0, x, chroma];
        else if (h < 300) [r, g, b] = [x, 0, chroma];
        else [r, g, b] = [chroma, 0, x];

        return this.rgbToHex(
            Math.round((r + m) * 255),
            Math.round((g + m) * 255),
            Math.round((b + m) * 255)
        );
    }

    private hexToHsv(hex: string): { h: number; s: number; v: number } {
        const r = Number.parseInt(hex.slice(1, 3), 16) / 255;
        const g = Number.parseInt(hex.slice(3, 5), 16) / 255;
        const b = Number.parseInt(hex.slice(5, 7), 16) / 255;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const delta = max - min;
        let h = 0;

        if (delta) {
            if (max === r) h = 60 * (((g - b) / delta) % 6);
            else if (max === g) h = 60 * ((b - r) / delta + 2);
            else h = 60 * ((r - g) / delta + 4);
        }

        return {
            h: Math.round(h < 0 ? h + 360 : h),
            s: Math.round(max === 0 ? 0 : (delta / max) * 100),
            v: Math.round(max * 100),
        };
    }

    private rgbToHex(r: number, g: number, b: number): string {
        return `#${[r, g, b]
            .map(value => value.toString(16).padStart(2, '0'))
            .join('')
            .toUpperCase()}`;
    }

    private updateColorPickerPosition(): void {
        const hostRect = (this._host.nativeElement as HTMLElement).getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const spacing = 12;
        const width = Math.min(320, viewportWidth - spacing * 2);
        const expectedHeight = Math.min(430, viewportHeight - spacing * 2);
        const spaceBelow = viewportHeight - hostRect.bottom - spacing;
        const top =
            spaceBelow >= expectedHeight
                ? hostRect.bottom + spacing
                : Math.max(spacing, hostRect.top - expectedHeight - spacing);
        const left = Math.min(
            Math.max(spacing, hostRect.left + (hostRect.width - width) / 2),
            viewportWidth - width - spacing
        );

        this.colorPickerPosition.set({
            top,
            left,
            width,
            maxHeight: viewportHeight - top - spacing,
        });
    }

    /**
     * Configura listeners para detectar quando o usuário sai da aba/janela.
     */
    private _setupWindowListeners(): void {
        this.windowBlurHandler = () => {
            if (this.hasFocus()) {
                this._handleLeave();
            }
        };
        window.addEventListener('blur', this.windowBlurHandler);

        this.visibilityChangeHandler = () => {
            if (document.hidden && this.hasFocus()) {
                this._handleLeave();
            }
        };
        document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    private _removeWindowListeners(): void {
        if (this.windowBlurHandler) {
            window.removeEventListener('blur', this.windowBlurHandler);
        }
        if (this.visibilityChangeHandler) {
            document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
        }
    }

    /**
     * Trata o evento de saída do editor (perda de foco).
     */
    private _handleLeave(): void {
        this._closeAutocomplete();
        this.hasFocus.set(false);
        const cleanContent = this._getCleanContent();
        this.lastEmittedValue = cleanContent;
        this.onLeave.emit(cleanContent);
    }
}
